import CompletedMetrics from './CompletedMetrics';
import SurveyorStats from '../../../components/SurveyorStats';
import { formatRating } from '../../../utils/rating';
import UnreadChatBadge from '../../../components/UnreadChatBadge';
import ReadyMetrics from "./ReadyMetrics";
import AdditionalMeasurementPanel from '../../../components/Quality/AdditionalMeasurementPanel';
import { useMemo, useState } from "react";
import useNow from "../../../hooks/useNow";
import { Box, Sheet, Stack, Typography, Chip, IconButton, Tooltip, Dropdown, Menu, MenuButton, MenuItem, Divider } from "@mui/joy";
import { keyframes } from "@emotion/react";

import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';



const pulseRed = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(229,57,53,0.55); }
  70%  { box-shadow: 0 0 0 9px rgba(229,57,53,0); }
  100% { box-shadow: 0 0 0 0 rgba(229,57,53,0); }
`;

// ── helpers ──
function eventStart(event) {
    // Приоритет — серверный start_at (с корректным offset таймзоны)
    if (event?.start_at) {
        const d = new Date(event.start_at);
        if (Number.isFinite(d.getTime())) return d;
    }
    if (!event?.date_event) return null;
    const t = event.time_event || event.time_from || '00:00:00';
    const d = new Date(`${event.date_event}T${t}`);
    return Number.isFinite(d.getTime()) ? d : null;
}
function humanLeft(ms) {
    const abs = Math.abs(ms);
    const m = Math.floor(abs / 60000);
    const days = Math.floor(m / 1440);
    const hours = Math.floor((m % 1440) / 60);
    const mins = m % 60;
    const parts = [];
    if (days) parts.push(`${days}д`);
    if (hours || days) parts.push(`${hours}ч`);
    parts.push(`${mins}м`);
    return parts.join(' ');
}
function fmtDuration(fromISO, toISO) {
    if (!fromISO || !toISO) return null;
    const ms = new Date(toISO).getTime() - new Date(fromISO).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return humanLeft(ms);
}

// Длительность из секунд (серверные *_duration_seconds)
function formatSeconds(sec) {
    if (sec == null || !Number.isFinite(sec) || sec < 0) return null;
    return humanLeft(sec * 1000);
}

// Таймер до начала (для колонок «На согласовании» / «Назначен обмер»)
function StartTimerChip({ event }) {
    const now = useNow();
    const start = useMemo(() => eventStart(event), [event]);
    if (!start) return null;
    const diff = start.getTime() - now;
    const overdue = diff < 0;
    return (
        <Chip
            size="sm"
            variant={overdue ? 'solid' : 'soft'}
            color={overdue ? 'danger' : (diff <= 6 * 3600000 ? 'warning' : 'primary')}
            startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 15 }} />}
            sx={{
                fontWeight: 700,
                ...(overdue && { animation: `${pulseRed} 1.6s infinite` }),
            }}
        >
            {overdue ? `Пересрок ${humanLeft(diff)}` : `Через ${humanLeft(diff)}`}
        </Chip>
    );
}

// Дедлайн черчения (для колонки «Взят в черчение»)
function DeadlineChip({ deadline }) {
    const now = useNow();

    // Раньше здесь было `if (!deadline) return null` — событие без дедлайна
    // выглядело точно так же, как событие с дедлайном, просто без метки.
    // Именно поэтому проблема с пропавшими дедлайнами так долго не всплывала.
    if (!deadline) {
        return (
            <Chip
                size="sm"
                variant="soft"
                color="danger"
                startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{ fontWeight: 700 }}
            >
                Дедлайн не задан
            </Chip>
        );
    }

    const diff = new Date(deadline).getTime() - now;
    if (!Number.isFinite(diff)) {
        return (
            <Chip size="sm" variant="soft" color="danger" sx={{ fontWeight: 700 }}>
                Дедлайн некорректен
            </Chip>
        );
    }
    const overdue = diff < 0;
    const color = overdue ? 'danger' : diff <= 6 * 3600000 ? 'danger' : diff <= 24 * 3600000 ? 'warning' : 'success';
    return (
        <Chip
            size="sm"
            variant="solid"
            color={color}
            startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 15 }} />}
            sx={{ fontWeight: 800, ...(overdue && { animation: `${pulseRed} 1.6s infinite` }) }}
        >
            {overdue ? `Просрочено ${humanLeft(diff)}` : `Дедлайн ${humanLeft(diff)}`}
        </Chip>
    );
}

const SUCCESS_LABELS = {
    yes: { label: 'Выезд состоялся', color: 'success' },
    moved: { label: 'Перенесён', color: 'warning' },
    postponed_to_date: { label: 'Перенос на дату', color: 'warning' },
    cancelled: { label: 'Отменён', color: 'danger' },
};

// Строка отзыва: подпись + звёзды + баллы
function ReviewStars({ label, value }) {
    if (!value || value <= 0) return null;
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.1}>
                {[1, 2, 3, 4, 5].map(i => i <= value
                    ? <StarRoundedIcon key={i} sx={{ fontSize: 13, color: 'warning.400' }} />
                    : <StarBorderRoundedIcon key={i} sx={{ fontSize: 13, color: 'neutral.300' }} />)}
                <Typography level="body-xs" sx={{ ml: 0.5, fontWeight: 700 }}>{value}/5</Typography>
            </Stack>
        </Stack>
    );
}

export default function KanbanEventCard({
    event, stage, dragHandleProps, isDragging,
    onOpen, onOpenChat, onOpenClientChat, onChangeStage, onDelete, onManageDraftsman, onToggleRevision, onAdditional,
}) {
    const [reviewOpen, setReviewOpen] = useState(false);

    const dealId = event?.bitrix_deal?.id;
    const address = event?.bitrix_deal?.full_address || event?.title || '';
    const manager = event?.manager;
    const surveyor = event?.employee;
    const draftsman = event?.draftsman;
    const firstMeasurement = ['measuring_assigned', 'closed_by_surveyor'].includes(stage) && surveyor?.surveyor_stats?.first_event_id === event.id;
    const q = event?.questionnaire;
    const de = event?.draftsmen_event;
    const deadline = event?.deadline_at
        || event?.drawing_deadline
        || event?.bitrix_deal?.dead_line_finish_drawing;

    // Приоритет — серверные длительности (точное сопоставление анкеты); иначе считаем сами
    const surveyorDur = event?.surveyor_duration_seconds != null
        ? formatSeconds(event.surveyor_duration_seconds)
        : fmtDuration(event?.date_create, q?.date_create);
    const draftsmanDur = event?.draftsman_duration_seconds != null
        ? formatSeconds(event.draftsman_duration_seconds)
        : fmtDuration(q?.date_create, de?.date_finish);

    return (
        <Sheet
            variant="outlined"
            onClick={() => onOpen?.(event)}
            sx={{
                borderRadius: '14px',
                ...(firstMeasurement ? { borderColor: 'warning.400', animation: 'firstMeasurementGlow 2s ease-in-out infinite', '@keyframes firstMeasurementGlow': { '0%,100%': { boxShadow: '0 0 0 1px #facc1544' }, '50%': { boxShadow: '0 0 0 4px #facc1577, 0 0 15px #facc1555' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none', outline: '2px solid #facc15' } } : {}),
                p: 1.25,
                cursor: 'pointer',
                bgcolor: event.is_additional_measuring ? '#fff7ed' : 'background.surface',
                ...(event.is_additional_measuring ? { borderColor: '#f59e0b', borderLeft: '4px solid #f59e0b' } : {}),
                boxShadow: isDragging ? '0 12px 28px rgba(87,42,195,0.22)' : '0 1px 4px rgba(15,23,42,0.05)',
                transition: 'box-shadow .15s ease, transform .15s ease',
                '&:hover': { boxShadow: '0 6px 18px rgba(87,42,195,0.14)' },
            }}
        >
            <AdditionalMeasurementPanel eventId={event.id} cycle={event.additional_measurement} />
            {event.additional_measurement?.status === 'completed' && <Chip size="sm" variant="soft" color="warning" sx={{ mb: 0.75 }}>После допобмера</Chip>}
            {/* Шапка: № + ручка перетаскивания */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography level="title-sm" sx={{ fontWeight: 800 }}>№{dealId ?? event?.id}</Typography>
                <Box
                    {...(dragHandleProps || {})}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        cursor: 'grab', color: 'text.tertiary', ml: 1,
                        display: 'flex', alignItems: 'center',
                        '&:active': { cursor: 'grabbing' },
                        '&:hover': { color: 'primary.500' },
                    }}
                >
                    <DragIndicatorRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
            </Stack>

            {/* Адрес */}
            <Typography level="body-sm" sx={{
                color: 'text.secondary', mb: 1,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
                {address}
            </Typography>

            {/* Чипы по стадиям */}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5, mb: 0.5 }}>
                {manager && (
                    <Chip size="sm" variant="soft" color="neutral" startDecorator={<PersonRoundedIcon sx={{ fontSize: 14 }} />}>
                        {manager.name} {manager.last_name || ''}
                    </Chip>
                )}

                {(stage === 'measuring_assigned' || stage === 'closed_by_surveyor'
                  || stage === 'in_drawing' || stage === 'completed') && surveyor && (
                    <Chip size="sm" variant="outlined" color="primary">
                        Обмер: {surveyor.name} {surveyor.last_name || ''}
                    </Chip>
                )}

                {(stage === 'in_drawing' || stage === 'completed') && draftsman && (
                    <Chip size="sm" variant="soft" color="warning" startDecorator={<DesignServicesRoundedIcon sx={{ fontSize: 14 }} />}>
                        {draftsman.name} {draftsman.last_name || ''}
                    </Chip>
                )}

                {(stage === 'pending_assignment' || stage === 'measuring_assigned') && (
                    <StartTimerChip event={event} />
                )}

                {stage === 'closed_by_surveyor'
                  && (event?.success_status || q?.status)
                  && SUCCESS_LABELS[event?.success_status || q?.status] && (
                    <Tooltip
                        size="sm"
                        title={
                            q?.reason_for_cancelation ? `Причина: ${q.reason_for_cancelation}`
                            : q?.transfer_reason ? `Причина переноса: ${q.transfer_reason}`
                            : ''
                        }
                    >
                        <Chip size="sm" variant="soft" color={SUCCESS_LABELS[event?.success_status || q?.status].color}>
                            {SUCCESS_LABELS[event?.success_status || q?.status].label}
                        </Chip>
                    </Tooltip>
                )}

                {/* Дедлайн идёт с момента закрытия обмерщиком, а не с момента,
                    когда чертёжник взял работу. В колонке «Закрыт обмерщиком»
                    просрочка копилась незаметно. */}
                {(stage === 'in_drawing' || stage === 'closed_by_surveyor') && (
                    <DeadlineChip deadline={deadline} />
                )}

                {(event?.client_rejected || event?.is_rework) && (
                    <Chip size="sm" variant="solid" color="danger"
                          startDecorator={<BuildRoundedIcon sx={{ fontSize: 14 }} />}>
                        {event?.client_rejected ? 'Клиент не принял работу' : 'Возврат на проверку'}
                    </Chip>
                )}
                {event?.is_in_revision && !event?.is_rework && (
                    <Chip size="sm" variant="soft" color="warning"
                          startDecorator={<BuildRoundedIcon sx={{ fontSize: 14 }} />}>
                        Доработка
                    </Chip>
                )}
            </Stack>

            {/* Подвал «Завершён»: скорость + рейтинг (раскрывается) */}
            {stage === 'completed' && (
                <Box sx={{ mt: 1.25 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                        {surveyorDur && (
                            <Chip size="sm" variant="soft" color="success" startDecorator={<SpeedRoundedIcon sx={{ fontSize: 14 }} />}>
                                Обмер: {surveyorDur}
                            </Chip>
                        )}
                        {draftsmanDur && (
                            <Chip size="sm" variant="soft" color="primary" startDecorator={<SpeedRoundedIcon sx={{ fontSize: 14 }} />}>
                                Черчение: {draftsmanDur}
                            </Chip>
                        )}
                        {event?.surveyor_rating != null && Number(event.surveyor_rating) > 0 && (
                            <Chip
                                size="sm" variant="soft" color="warning"
                                onClick={(e) => { e.stopPropagation(); setReviewOpen(o => !o); }}
                                startDecorator={<StarRoundedIcon sx={{ fontSize: 14 }} />}
                                endDecorator={<ExpandMoreRoundedIcon sx={{
                                    fontSize: 14,
                                    transform: reviewOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform .2s',
                                }} />}
                                sx={{ cursor: 'pointer' }}
                            >
                                {formatRating(event.surveyor_rating)}
                            </Chip>
                        )}
                    </Stack>

                    {reviewOpen && event?.surveyor_review && (
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mt: 1, p: 1, borderRadius: 'md', bgcolor: 'background.level1' }}
                        >
                            <ReviewStars label="Качество ТЗ" value={event.surveyor_review.stars_quality_TZ} />
                            <ReviewStars label="Качество" value={event.surveyor_review.stars_quality} />
                            <ReviewStars label="Отклик" value={event.surveyor_review.stars_response} />
                            {event.surveyor_review.comment && (
                                <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.secondary', fontStyle: 'italic' }}>
                                    «{event.surveyor_review.comment}»
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {surveyor && <Box sx={{ my: 1 }}><SurveyorStats employee={surveyor} />{firstMeasurement && <Chip size="sm" color="warning" sx={{ mt: 0.5 }}>Первый обмер в системе</Chip>}</Box>}

            {stage === 'ready_for_delivery' && <ReadyMetrics metrics={event?.ready_metrics} />}
            {stage === 'completed' && <CompletedMetrics metrics={event?.completed_metrics} />}

            {/* Подвал «Отменён»: причина */}
            {stage === 'cancelled' && (
                <Typography level="body-xs" sx={{ color: 'danger.500', mt: 0.5 }}>
                    Причина: {event?.cancel_reason || q?.reason_for_cancelation || q?.transfer_reason || '—'}
                </Typography>
            )}

            {Number(event?.transfer_amount || 0) > 0 && (
                <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.65rem' }}>
                    выезд {Number(event.transfer_amount).toLocaleString('ru-RU')} ₽
                    {event?.transfer_is_manual ? ' (вручную)' : ''}
                </Chip>
                )}

                {/* Исполнитель назначен, а задание в Консоли не создалось */}
                {event?.konsol_issue && (
                <Tooltip size="sm" variant="soft" title={event.konsol_issue}>
                    <Chip size="sm" variant="solid" color="danger" sx={{ fontSize: '0.65rem' }}>
                    Консоль: сбой
                    </Chip>
                </Tooltip>
            )}

            {/* Действия: быстрые кнопки + скрытое меню */}
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end"
                   sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                {['closed_by_surveyor', 'in_drawing', 'quality_check'].includes(stage) && !event.is_additional_measuring && <Tooltip title="Отправить на допобмер"><IconButton size="sm" variant="soft" color="warning" aria-label="Допобмер" onClick={() => onAdditional?.(event)}><BuildRoundedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                {event?.client_chat?.id && (
                    <Tooltip title="Чат с клиентом" variant="soft">
                        <IconButton size="sm" variant="outlined" color="neutral"
                                    onClick={() => onOpenClientChat?.(event)} sx={{ borderRadius: 'md' }}>
                            <UnreadChatBadge chatId={event?.client_chat?.id}><ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>
                        </IconButton>
                    </Tooltip>
                )}
                {event && (
                    <Tooltip title="Чат команды" variant="soft">
                        <IconButton size="sm" variant="soft" color="primary"
                                    onClick={() => onOpenChat?.(event)} sx={{ borderRadius: 'md' }}>
                            <UnreadChatBadge chatId={event?.event_chat?.id}><ForumRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>
                        </IconButton>
                    </Tooltip>
                )}
                <Dropdown>
                    <MenuButton slots={{ root: IconButton }}
                                slotProps={{ root: { size: 'sm', variant: 'plain', color: 'neutral' } }}>
                        <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
                    </MenuButton>
                    <Menu size="sm" placement="bottom-end" sx={{ zIndex: 10102 }}>
                        <MenuItem onClick={() => onOpen?.(event)}>
                            <VisibilityRoundedIcon sx={{ fontSize: 18 }} /> Открыть событие
                        </MenuItem>
                        <MenuItem onClick={() => onChangeStage?.(event)}>
                            <AltRouteRoundedIcon sx={{ fontSize: 18 }} /> Сменить стадию
                        </MenuItem>
                        {/* Раньше пункт был только в «Закрыт замерщиком» и «Взят в
                            черчение». В «Работа не принята» и «Завершён» его не было —
                            а менять чертёжника чаще всего нужно именно на доработке.
                            Сервер такие действия теперь разрешает (event_draftsman_*
                            больше не требует строго state='drawing'). */}
                        {['closed_by_surveyor', 'in_drawing', 'quality_check', 'ready_for_delivery', 'completed'].includes(stage) && (
                            <MenuItem onClick={() => onManageDraftsman?.(event)}>
                                <DesignServicesRoundedIcon sx={{ fontSize: 18 }} /> {event?.date_completed || stage === 'completed' ? 'Повторное черчение / сменить чертёжника' : 'Чертёжник'}
                            </MenuItem>
                        )}
                        {event?.client_chat?.id && (
                            <MenuItem onClick={() => onOpenClientChat?.(event)}>
                                <UnreadChatBadge chatId={event?.client_chat?.id}><ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} /></UnreadChatBadge> Чат с клиентом
                            </MenuItem>
                        )}
                        {event && (
                            <MenuItem onClick={() => onOpenChat?.(event)}>
                                <UnreadChatBadge chatId={event?.event_chat?.id}><ForumRoundedIcon sx={{ fontSize: 18 }} /></UnreadChatBadge> Чат команды
                            </MenuItem>
                        )}
                        {['closed_by_surveyor', 'in_drawing', 'quality_check'].includes(stage) && !event.is_additional_measuring && (
                            <MenuItem color="warning" onClick={() => onAdditional?.(event)}><BuildRoundedIcon sx={{ fontSize: 18 }} /> Допобмер</MenuItem>
                        )}
                        <Divider />
                        <MenuItem color="danger" onClick={() => onDelete?.(event)}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /> Удалить событие
                        </MenuItem>
                    </Menu>
                </Dropdown>
            </Stack>
        </Sheet>
    );
}